import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// ─── httpOnly cookie auth (post-Feb-2026 migration) ────────────────
//
// The backend sets three httpOnly cookies on login / register / Google
// callback: `access_token`, `refresh_token`, and (for Google users)
// `session_token`. All three are `Secure` + `SameSite=None` so they
// travel with cross-origin requests (frontend at www.ogmini.com, API at
// ogmini-next.emergent.host). Nothing in the browser JS can read them —
// that's the whole point: an XSS payload can no longer exfiltrate the
// session the way it could when the JWT was mirrored to localStorage.
//
// To get the browser to attach those cookies to XHR/fetch requests we
// have to opt in per-request via `withCredentials: true` (axios) or
// `credentials: 'include'` (fetch). Flipping it globally is safe here
// because our CORS is configured with `allow_credentials=True` +
// explicit `allow_origins` (no wildcard), which is the only combo the
// browser accepts for credentialed cross-origin requests.
axios.defaults.withCredentials = true;

// One-time cleanup of the legacy Bearer token that used to live in
// localStorage. Idempotent — runs at module load, no-op after the first
// visit post-migration. Keeps XSS-readable credentials from lingering
// in older browsers.
try { localStorage.removeItem('token'); } catch (_) { /* private mode */ }

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    // The user object itself is NOT a credential — it's the response
    // payload from /auth/me (id, name, email, role, permissions). Keeping
    // the last-known copy in localStorage prevents the nav bar from
    // flashing "Sign in" on every page reload while the httpOnly cookie
    // is still valid and /auth/me is in flight.
    try {
      const cached = localStorage.getItem('user');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [wishlistIds, setWishlistIds] = useState(new Set());

  useEffect(() => {
    if (user) localStorage.setItem('user', JSON.stringify(user));
    else      localStorage.removeItem('user');
  }, [user]);

  // Legacy shim: components (and one raw fetch call) used to spread
  // authHeaders() into request options to get the `Authorization`
  // header. With httpOnly cookies the browser attaches credentials
  // automatically, so this returns an empty object. Kept around so
  // existing call sites don't have to change in the same PR.
  const authHeaders = useCallback(() => ({}), []);

  const fetchWishlistIds = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/wishlist`);
      const ids = new Set((data.products || []).map((p) => p.id));
      setWishlistIds(ids);
    } catch (e) {
      console.error('wishlist fetch failed', e);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      fetchWishlistIds();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchWishlistIds]);

  useEffect(() => {
    // Initial auth check on mount — inline IIFE so strict
    // `set-state-in-effect` lint doesn't flag the setState chain
    // through `checkAuth`. Same auth/me roundtrip, same outcome.
    //
    // CRITICAL: If returning from Emergent Google Auth callback, skip
    // the /me check entirely. AuthCallback will exchange session_id
    // and establish the session first — hitting /me here races that
    // exchange and produces a false 401 flash on the login screen.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return () => {};
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        if (!cancelled) { setUser(data); fetchWishlistIds(); }
      } catch (e) {
        const status = e?.response?.status;
        if (!cancelled && (status === 401 || status === 403)) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchWishlistIds]);

  const login = async (email, password) => {
    // Backend sets `access_token` + `refresh_token` as httpOnly cookies
    // via the Set-Cookie response header. Because `withCredentials` is
    // on globally (see module init above), every subsequent request the
    // browser makes to the API carries those cookies automatically —
    // nothing else in the app needs to know about the token.
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
    fetchWishlistIds();
    return data;
  };

  const register = async (name, email, password, phone) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password, phone });
    setUser(data);
    fetchWishlistIds();
    return data;
  };

  const logout = async () => {
    // Backend clears both the JWT cookies and (defensively) the Google
    // session cookie so a returning customer sees the login screen
    // instead of getting silently re-authenticated by a still-valid
    // session_token.
    try { await axios.post(`${API}/auth/logout`, {}); } catch (e) { console.error('logout failed', e); }
    try { await axios.post(`${API}/auth/google/logout`, {}); } catch (_) { /* non-fatal */ }
    setUser(null);
    setWishlistIds(new Set());
  };

  const toggleWishlist = useCallback(async (productId) => {
    if (!user) return { ok: false, reason: 'login_required' };
    const isIn = wishlistIds.has(productId);
    setWishlistIds((prev) => {
      const next = new Set(prev);
      if (isIn) next.delete(productId); else next.add(productId);
      return next;
    });
    try {
      if (isIn) {
        await axios.delete(`${API}/wishlist/${productId}`);
      } else {
        await axios.post(`${API}/wishlist/${productId}`, {});
      }
      return { ok: true, added: !isIn };
    } catch (e) {
      console.error('wishlist toggle failed', e);
      setWishlistIds((prev) => {
        const next = new Set(prev);
        if (isIn) next.add(productId); else next.delete(productId);
        return next;
      });
      return { ok: false, reason: 'network' };
    }
  }, [user, wishlistIds]);

  const isInWishlist = useCallback((productId) => wishlistIds.has(productId), [wishlistIds]);

  return (
    <AuthContext.Provider value={{
      user, loading,
      login, register, logout, checkAuth, authHeaders,
      wishlistIds, toggleWishlist, isInWishlist, refreshWishlist: fetchWishlistIds,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
